'use client';

import * as React from 'react';
import { Check, ChevronsUpDown } from 'lucide-react';

import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from '@/components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { FIELD_CHROME, type FieldVariant } from '@/lib/field-chrome';
import { useFieldVariant } from '@/components/ui/field-variant';

export interface ComboboxOption {
  value: string;
  label: string;
  group?: string;
  description?: string;
  searchValue?: string;
}

interface ComboboxProps {
  options: ComboboxOption[];
  value?: string;
  onSelect: (value: string) => void;
  placeholder?: string;
  searchPlaceholder?: string;
  emptyText?: string;
  className?: string;
  /** Visual variant. Defaults to the nearest `FieldVariantProvider`, or `'solid'`. */
  variant?: FieldVariant;
}

const COMBOBOX_VARIANT = {
  solid: `${FIELD_CHROME.solid.base} ${FIELD_CHROME.solid.focusVisible}`,
  glass: `${FIELD_CHROME.glass.base} ${FIELD_CHROME.glass.focusVisible}`,
} as const;

export function Combobox({
  options,
  value,
  onSelect,
  placeholder = 'Select option...',
  searchPlaceholder = 'Search...',
  emptyText = 'No option found.',
  className,
  variant,
}: ComboboxProps) {
  const [open, setOpen] = React.useState(false);
  const resolvedVariant = useFieldVariant(variant);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className={cn(
            'w-full justify-between font-normal',
            COMBOBOX_VARIANT[resolvedVariant],
            value
              ? 'text-foreground dark:text-neutral-100'
              : 'text-muted-foreground dark:text-neutral-400',
            className
          )}
        >
          <span className="truncate">
            {value
              ? options.find((option) => option.value === value)?.label || placeholder
              : placeholder}
          </span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="w-[var(--radix-popover-trigger-width)] p-0"
        align="center"
        side="bottom"
        sideOffset={4}
      >
        <Command>
          <CommandInput placeholder={searchPlaceholder} />
          <CommandList>
            <CommandEmpty>{emptyText}</CommandEmpty>
            {(() => {
              const groups = options.reduce(
                (acc, option) => {
                  const group = option.group || '';
                  if (!acc[group]) acc[group] = [];
                  acc[group].push(option);
                  return acc;
                },
                {} as Record<string, ComboboxOption[]>
              );

              const groupKeys = Object.keys(groups);

              return groupKeys.map((group, index) => (
                <React.Fragment key={group || 'default'}>
                  <CommandGroup heading={group || undefined}>
                    {groups[group].map((option) => (
                      <CommandItem
                        key={option.value}
                        value={option.searchValue || option.label}
                        onSelect={() => {
                          onSelect(option.value);
                          setOpen(false);
                        }}
                      >
                        <Check
                          className={cn(
                            'mr-2 h-4 w-4 shrink-0',
                            value === option.value ? 'opacity-100' : 'opacity-0'
                          )}
                        />
                        <div className="flex flex-col min-w-0 flex-1">
                          <span className="truncate">{option.label}</span>
                          {option.description && (
                            <span className="truncate text-xs text-muted-foreground">
                              {option.description}
                            </span>
                          )}
                        </div>
                      </CommandItem>
                    ))}
                  </CommandGroup>
                  {index < groupKeys.length - 1 && <CommandSeparator />}
                </React.Fragment>
              ));
            })()}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
